import {
  type CSSProperties,
  type UIEventHandler,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import {
  Box,
  Checkbox,
  CircularProgress,
  Stack,
  type SxProps,
  useTheme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import {
  type ListChildComponentProps,
  type ListOnScrollProps,
  VariableSizeList,
} from 'react-window';

import {
  useKanbanInstanceId,
  useKanbanSelectedState,
} from '@modules/_shared/components/kanban/KanbanBoard';
import { KanbanItem } from '@modules/_shared/components/kanban/KanbanItem';
import { useLocale } from '@shared/hooks/locale';

type KanbanColumnProps<Item> = {
  title: React.ReactNode;
  colId: string;
  items: Item[];
  index: number;
  width?: number;
  topControls?: React.ReactNode;
  endControls?: React.ReactNode;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  renderItem: (data: Item) => React.ReactNode;
  getItemId: (item: Item) => string;
  canDragItem?: (data: Item) => boolean;
  getItemBorderColor?: (data: Item) => string | undefined;
  isDroppable?: boolean;
  isReorderable?: boolean;
  onItemClicked?: (item: Item) => void;
  onClickHeaderCheckbox?: () => void;
  onScroll?: () => void;
  onVisibilityChange?: (visible: boolean) => void;
  onHeightCalculated?: (height: number, index: number) => void;
  customOverStyle?: SxProps;
};

type TitleProps = {
  label: React.ReactNode;
  hideArrow?: boolean;
  hideCheckbox?: boolean;
  itemsIds: string[];
  onClickHeaderCheckbox?: () => void;
};

const TITLE_HEIGHT = 40;
const ARR_HEIGHT = TITLE_HEIGHT / Math.sqrt(2);

const Title = ({
  label,
  hideArrow = false,
  itemsIds,
  hideCheckbox = false,
  onClickHeaderCheckbox,
}: TitleProps) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const { selectedItems, setSelectedItems } = useKanbanSelectedState();
  const border = `1px solid ${palette.grey[200]}`;

  const arrow = {
    content: '""',
    position: 'absolute',
    borderTop: border,
    borderRight: border,
    left: -ARR_HEIGHT / 1.8 + 2,
    top: (TITLE_HEIGHT - ARR_HEIGHT) / 2 - 1,
    transformOrigin: `${ARR_HEIGHT / 2}px ${ARR_HEIGHT / 2}px`,
    width: ARR_HEIGHT,
    height: ARR_HEIGHT,
    transform: 'scale(0.55, 1) rotateZ(45deg)',
  } as SystemStyleObject;

  const columnSelectionState = useMemo(() => {
    if (selectedItems == null) {
      return undefined;
    }

    const nbSelectedItems = itemsIds.filter(itemId =>
      selectedItems.includes(itemId),
    ).length;

    if (nbSelectedItems === 0) {
      return 'none';
    }

    if (nbSelectedItems === itemsIds.length) {
      return 'all';
    }

    return 'some';
  }, [itemsIds, selectedItems]);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        pl: 4,
        pr: 2,
        height: TITLE_HEIGHT,
        minHeight: TITLE_HEIGHT,
        position: 'relative',
        overflow: 'hidden',
        borderTop: border,
        borderBottom: border,
        background: 'var(--mui-palette-background-paper)',
        fontWeight: 600,
        '&:before': hideArrow ? null : arrow,
      }}
    >
      {!hideCheckbox && (
        <Checkbox
          size="small"
          sx={{ p: 0, mr: 0.75 }}
          disabled={itemsIds.length === 0}
          checked={columnSelectionState === 'all'}
          indeterminate={columnSelectionState === 'some'}
          onClick={onClickHeaderCheckbox}
          onChange={event => {
            if (setSelectedItems == null || selectedItems == null) {
              return;
            }
            if (event.target.checked) {
              setSelectedItems([...new Set([...selectedItems, ...itemsIds])]);
            } else {
              setSelectedItems(
                selectedItems.filter(id => !itemsIds.includes(id)),
              );
            }
          }}
        />
      )}
      {label}
    </Stack>
  );
};

type VirtualizedRowData<Item> = Pick<
  KanbanColumnProps<Item>,
  | 'getItemId'
  | 'renderItem'
  | 'onItemClicked'
  | 'colId'
  | 'items'
  | 'hasMore'
  | 'isLoadingMore'
  | 'onHeightCalculated'
  | 'canDragItem'
  | 'getItemBorderColor'
> & { isDropTarget?: boolean };

const VirtualizedRow = <Item,>({
  index,
  data,
  style,
}: ListChildComponentProps<VirtualizedRowData<Item>>) => {
  const { t } = useLocale();

  const {
    getItemId,
    renderItem,
    onItemClicked,
    colId,
    onHeightCalculated,
    items,
    hasMore,
    isLoadingMore,
    canDragItem,
    getItemBorderColor,
    isDropTarget,
  } = data;

  const onHeightCalc = useCallback(
    (height: number) => onHeightCalculated?.(height, index),
    [index, onHeightCalculated],
  );

  if (index === items.length) {
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
        style={style}
      >
        {isLoadingMore && hasMore ? (
          <CircularProgress disableShrink size={20} />
        ) : (
          t('No more items to load')
        )}
      </Box>
    );
  }

  const item = items[index];
  const id = getItemId(item);

  return (
    <div style={{ ...style, paddingLeft: 4, paddingRight: 4 }}>
      <KanbanItem
        renderItem={renderItem}
        data={item}
        itemId={id}
        columnId={colId}
        key={id}
        onHeightCalculated={onHeightCalc}
        canDrag={canDragItem?.(item)}
        isDropTarget={isDropTarget}
        getBorderColor={getItemBorderColor}
        onClick={
          onItemClicked != null
            ? () => {
                onItemClicked(item);
              }
            : undefined
        }
      />
    </div>
  );
};

const VirtualizedListOuterEl = forwardRef<
  HTMLDivElement,
  {
    onScroll: UIEventHandler<HTMLDivElement>;
    style: CSSProperties;
    className?: string;
    children: React.ReactNode;
  }
>((props, ref) => {
  const context = useContext(BackgroundContext);
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const background = context?.background ?? palette.grey[100];

  return (
    <Box
      onScroll={props.onScroll}
      sx={{
        ...props.style,
        // In WebKit we customize scrollbar,
        // in other engines just hide it
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { width: 4, background },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 3,
          background: 'grey.300',
          border: `1px solid ${background}`,
        },
        '&::-webkit-scrollbar-track': { background },
      }}
      ref={ref}
    >
      {props.children}
    </Box>
  );
});

const BackgroundContext = createContext<null | { background: string }>(null);

const SCROLL_THRESHOLD = 100; // pixels from bottom to trigger loading

export const KanbanColumn = <Item,>({
  title,
  colId,
  width = 336,
  index,
  topControls,
  items,
  renderItem,
  getItemId,
  onItemClicked,
  endControls,
  onClickHeaderCheckbox,
  onScroll,
  isLoadingMore,
  hasMore,
  onVisibilityChange,
  canDragItem,
  getItemBorderColor,
  isDroppable = true,
  isReorderable = true,
  customOverStyle = {},
}: KanbanColumnProps<Item>) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const itemHeightsRef = useRef<number[]>([]);
  const virtualizedListRef = useRef<VariableSizeList | null>(null);
  const columnInnerRef = useRef<HTMLDivElement | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const [isItemOver, setIsItemOver] = useState(false);
  const instanceId = useKanbanInstanceId();

  const isFirstColumn = index === 0;
  const border = `1px solid ${palette.grey[200]}`;
  const borderLeft = isFirstColumn ? undefined : border;
  const widthWithBorder = borderLeft == null ? width : width + 1;
  const background = isItemOver ? palette.grey[200] : palette.grey[100];
  const itemsIds = useMemo(() => items.map(getItemId), [items, getItemId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        onVisibilityChange?.(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    if (columnInnerRef.current) {
      observer.observe(columnInnerRef.current);
    }

    return () => observer.disconnect();
  }, [onVisibilityChange]);

  useEffect(() => {
    if (columnInnerRef.current == null || scrollableRef.current == null) {
      return;
    }

    return combine(
      dropTargetForElements({
        element: columnInnerRef.current,
        getData: () => ({ columnId: colId, type: 'column' }),
        canDrop: ({ source }) => {
          if (!isDroppable) {
            return false;
          }

          return (
            source.data.instanceId === instanceId && source.data.type === 'card'
          );
        },
        getIsSticky: () => true,
        onDragEnter: () => setIsItemOver(true),
        onDragLeave: () => setIsItemOver(false),
        onDragStart: () => setIsItemOver(true),
        onDrop: () => setIsItemOver(false),
      }),
      autoScrollForElements({
        element: scrollableRef.current,
        canScroll: ({ source }) =>
          source.data.instanceId === instanceId && source.data.type === 'card',
      }),
    );
  }, [instanceId, colId, isDroppable]);

  const onItemHeightCalculated = useCallback(
    (height: number, itemIndex: number) => {
      if (itemHeightsRef.current[itemIndex] === height) {
        //  We do not update if the height is the same.
        return;
      }
      itemHeightsRef.current[itemIndex] = height;

      if (virtualizedListRef.current != null) {
        virtualizedListRef.current.resetAfterIndex(itemIndex);
      }
    },
    [],
  );

  const handleScroll = ({
    scrollOffset,
    scrollUpdateWasRequested,
  }: ListOnScrollProps) => {
    if (scrollUpdateWasRequested) {
      return;
    }

    const scrollableElement = scrollableRef.current;
    if (!scrollableElement) {
      return;
    }

    const isNearBottom =
      scrollableElement.scrollHeight -
        scrollOffset -
        scrollableElement.clientHeight <
      SCROLL_THRESHOLD;

    if (isNearBottom && onScroll) {
      onScroll();
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: 'column',
        minWidth: widthWithBorder,
        maxWidth: widthWithBorder,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Title
        onClickHeaderCheckbox={onClickHeaderCheckbox}
        hideArrow={isFirstColumn}
        label={title}
        itemsIds={itemsIds}
        hideCheckbox={!isDroppable}
      />
      <Stack
        ref={columnInnerRef}
        sx={{
          flexGrow: 1,
          background,
          borderLeft,
          minHeight: 0,
          transition: 'background 0.2s',
          ...(isItemOver ? customOverStyle : {}),
        }}
      >
        {topControls != null && (
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: border,
            }}
          >
            {topControls}
          </Box>
        )}
        <Box
          ref={virtualizedListContainerRef}
          sx={{
            flex: 1,
            height: '100%',
            minHeight: 0,
          }}
        >
          <BackgroundContext.Provider value={{ background }}>
            <VariableSizeList<VirtualizedRowData<Item>>
              outerElementType={VirtualizedListOuterEl}
              ref={virtualizedListRef}
              outerRef={scrollableRef}
              onScroll={handleScroll}
              width="100%"
              height={virtualizedListContainerRef.current?.clientHeight ?? 725}
              estimatedItemSize={80}
              itemSize={itemIndex => itemHeightsRef.current[itemIndex] ?? 80}
              itemCount={items.length + (onScroll != null ? 1 : 0)}
              overscanCount={30}
              itemData={{
                items,
                getItemId,
                renderItem,
                onItemClicked,
                colId,
                onHeightCalculated: onItemHeightCalculated,
                hasMore,
                isLoadingMore,
                canDragItem,
                getItemBorderColor,
                isDropTarget: isDroppable && isReorderable,
              }}
            >
              {VirtualizedRow}
            </VariableSizeList>
          </BackgroundContext.Provider>
        </Box>
        {endControls != null && (
          <Box
            sx={{
              flexShrink: 0,
            }}
          >
            {endControls}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
